import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Icons } from "../lib/icons";
import { Btn, Modal } from "../lib/primitives";
import { SaveBar } from "../components/SaveBar";
import { apiClient, ApiError } from "../lib/api";
import { useOperatorRole, useProvider } from "../lib/hooks";
import { ConnectionBanner } from "../components/ConnectionBanner";

export function Settings() {
  const [tab, setTab] = useState("org");
  // Danger-zone actions are owner-only (#53) — don't show the tab to others.
  const { isOwner } = useOperatorRole();
  const tabs: Array<[string, string]> = isOwner
    ? [["org", "Organization"], ["danger", "Danger zone"]]
    : [["org", "Organization"]];
  return (
    <>
      <ConnectionBanner />
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Account</div>
          <h1 className="ad-h1">Settings</h1>
          <div className="ad-sub">Organization details{isOwner ? " and the danger zone" : ""}.</div>
        </div>
      </div>

      <div className="ad-tabs">
        {tabs.map(([id, l]) => (
          <div key={id} className={`ad-tab ${tab === id ? "is-active" : ""}`} onClick={() => setTab(id)}>{l}</div>
        ))}
      </div>

      {tab === "org" && (
        <div className="ad-stack-lg" style={{ maxWidth: 720 }}>
          <OrgCard />
        </div>
      )}
      {tab === "danger" && isOwner && (
        <div className="ad-stack-lg" style={{ maxWidth: 720 }}>
          <DangerZone />
        </div>
      )}
    </>
  );
}

/**
 * The one genuinely editable setting today: the organization display name,
 * persisted via PATCH /api/provider (owner/admin only — the server enforces
 * the role; viewers see the 403 surfaced as an error). Provider ID and rate
 * limit are read-only real values.
 */
function OrgCard() {
  const queryClient = useQueryClient();
  // Viewers see the values read-only (#53); the server enforces RBAC on PATCH.
  const { canWrite } = useOperatorRole();
  const { data: provider, isLoading } = useProvider();
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (provider && name === null) setName(provider.name);
  }, [provider, name]);

  const mutation = useMutation({
    mutationFn: (n: string) => apiClient.provider.update({ name: n }),
    onSuccess: (saved) => {
      setError(null);
      setSuccess("Saved.");
      setName(saved.name);
      queryClient.setQueryData(["provider"], saved);
      window.setTimeout(() => setSuccess(null), 2000);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to save");
    },
  });

  if (isLoading || name === null) {
    return (
      <div className="ad-card">
        <div className="ad-card-head"><div className="ad-card-title">Organization</div></div>
        <div style={{ fontSize: 12, color: "var(--ad-fg-3)" }}>Loading…</div>
      </div>
    );
  }

  const dirty = name.trim().length > 0 && name.trim() !== (provider?.name ?? "");

  function onSave() {
    if (!dirty || mutation.isPending) return;
    mutation.mutate(name!.trim());
  }

  return (
    <div className="ad-card">
      <div className="ad-card-head"><div className="ad-card-title">Organization</div></div>
      <div className="ad-stack-md">
        <div className="ad-field">
          <div className="ad-field-label">Organization name</div>
          <input
            className="ad-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            maxLength={120}
            disabled={!canWrite}
          />
        </div>
        <div className="ad-field">
          <div className="ad-field-label">Provider ID</div>
          <input className="ad-input mono" value={provider?.id ?? ""} readOnly />
        </div>
        <div className="ad-field">
          <div className="ad-field-label">Rate limit</div>
          <input className="ad-input mono" value={provider ? `${provider.rateLimitMax} / min` : ""} readOnly />
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 6, fontSize: 12, background: "rgba(220, 38, 38, 0.08)", color: "#b91c1c" }}>
          {error}
        </div>
      )}

      {/* Save/Discard moved to the contextual bottom SaveBar. */}
      {canWrite && success && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <span style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>{success}</span>
        </div>
      )}

      <SaveBar
        visible={canWrite && dirty}
        pending={mutation.isPending}
        onDiscard={() => { setName(provider?.name ?? ""); setError(null); }}
        onPublish={onSave}
        label="Save"
        pendingLabel="Saving…"
      />
    </div>
  );
}

/**
 * Owner-only org delete (the tab is already gated). Soft-delete on the
 * server: revokes every api_key, removes all memberships, drops pending
 * invites, parks the provider at status='deleted', and writes a
 * `provider.deleted` audit row — all in one transaction. The modal
 * requires typing the organization's exact name.
 */
function DangerZone() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: provider } = useProvider();
  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.provider.remove(confirmName.trim()),
    onSuccess: () => {
      // Everything cached belongs to the org that no longer exists.
      queryClient.clear();
      navigate("/welcome", { replace: true });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to delete organization");
    },
  });

  const nameMatches = provider != null && confirmName.trim() === provider.name;

  return (
    <>
      <div className="ad-card" style={{ border: "1px solid color-mix(in oklab, var(--ad-error) 40%, transparent)" }}>
        <div className="ad-card-head"><div className="ad-card-title" style={{ color: "var(--ad-error)" }}>Danger zone</div></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ad-fg-1)" }}>Delete this organization</div>
            <div style={{ fontSize: 12, color: "var(--ad-fg-3)" }}>
              Revokes all API keys and removes every member. End users are unaffected (they own their factors).
              This cannot be undone from the dashboard.
            </div>
          </div>
          <Btn kind="danger" leading={<Icons.Trash size={14} />} onClick={() => { setConfirmName(""); setError(null); setOpen(true); }}>
            Delete org
          </Btn>
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="This can't be undone"
        title="Delete this organization?"
        footer={
          <>
            <Btn kind="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn
              kind="danger"
              leading={<Icons.Trash size={14} />}
              disabled={!nameMatches || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete forever"}
            </Btn>
          </>
        }
      >
        <div className="ad-stack-md">
          <div className="ad-sub">
            Every API key is revoked immediately (live traffic stops), all team members lose access,
            and pending invitations are voided. Authentication data stays in the audit log for compliance.
          </div>
          <div className="ad-field">
            <div className="ad-field-label">
              Type <strong style={{ color: "var(--ad-fg-1)" }}>{provider?.name ?? "…"}</strong> to confirm
            </div>
            <input
              className="ad-input"
              value={confirmName}
              onChange={(e) => { setConfirmName(e.target.value); setError(null); }}
              placeholder={provider?.name ?? ""}
              autoFocus
            />
          </div>
          {error && (
            <div style={{ padding: 8, borderRadius: 6, fontSize: 12, background: "rgba(220, 38, 38, 0.08)", color: "#b91c1c" }}>
              {error}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
